import { useState, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  buildPersonalityMap,
  buildCompetenciesMap,
  buildMotivationMap,
  buildCareerMap,
} from '../utils/mindmapBuilder'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.22)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.1)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  textDim:   '#6a6e85',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

// ─── Node types ───────────────────────────────────────────────────────────────

function CenterNode({ data }) {
  return (
    <>
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{
        background: 'rgba(180,185,230,0.10)',
        border: `1.5px solid rgba(180,185,230,0.55)`,
        borderRadius: 16,
        padding: '18px 28px',
        textAlign: 'center',
        boxShadow: '0 0 40px rgba(180,185,230,0.12), 0 0 80px rgba(180,185,230,0.05)',
        minWidth: 160,
        maxWidth: 240,
      }}>
        <div style={{ fontFamily: font.heading, fontSize: 30, color: '#f0f2ff', fontWeight: 400, lineHeight: 1.2 }}>
          {data.label}
        </div>
        {data.sublabel && (
          <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textMuted, marginTop: 8, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 }}>
            {data.sublabel}
          </div>
        )}
      </div>
    </>
  )
}

function BranchNode({ data }) {
  return (
    <>
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{
        background: 'rgba(180,185,230,0.08)',
        border: `1px solid rgba(180,185,230,0.38)`,
        borderRadius: 10,
        padding: '9px 18px',
        fontFamily: font.body,
        fontSize: 15,
        fontWeight: 600,
        color: '#ced3f0',
        textAlign: 'center',
        minWidth: 130,
        maxWidth: 210,
        boxShadow: '0 2px 16px rgba(0,0,0,0.6)',
        letterSpacing: '0.03em',
      }}>
        {data.label}
      </div>
    </>
  )
}

function LeafNode({ data }) {
  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid rgba(180,185,230,0.18)`,
        borderRadius: 6,
        padding: '6px 13px',
        fontFamily: font.body,
        fontSize: 14,
        color: '#a8afc8',
        maxWidth: 200,
        lineHeight: 1.45,
      }}>
        {data.label}
      </div>
    </>
  )
}

const nodeTypes = {
  mindmapCenter: CenterNode,
  mindmapBranch: BranchNode,
  mindmapLeaf:   LeafNode,
}

// ─── Map view ─────────────────────────────────────────────────────────────────

function MapView({ graphData }) {
  const [nodes, , onNodesChange] = useNodesState(graphData.nodes)
  const [edges, , onEdgesChange] = useEdgesState(graphData.edges)

  return (
    <div style={{ width: '100%', height: '100%', background: C.bg }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        minZoom={0.15}
        maxZoom={2.5}
        style={{ background: 'transparent' }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="rgba(180,185,230,0.05)" gap={32} size={1} />
        <Controls
          showInteractive={false}
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${C.border}`,
            borderRadius: 8,
          }}
        />
      </ReactFlow>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'personality',  label: 'Личность' },
  { key: 'competencies', label: 'Компетенции' },
  { key: 'motivation',   label: 'Мотивация' },
  { key: 'career',       label: 'Карьера' },
]

export default function Mindmap({ profile }) {
  const [tab, setTab] = useState('personality')

  const graphs = useMemo(() => ({
    personality:  buildPersonalityMap(profile),
    competencies: buildCompetenciesMap(profile),
    motivation:   buildMotivationMap(profile),
    career:       buildCareerMap(profile),
  }), [profile])

  const hasData = profile && (
    profile.personality_data || profile.personality ||
    profile.competencies_data || profile.competencies
  )

  if (!hasData) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px', color: C.textMuted, fontFamily: font.body }}>
        <div style={{ fontFamily: font.mono, fontSize: 28, marginBottom: 16, opacity: 0.3 }}>◎</div>
        <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>Нет данных для карты</div>
        <div style={{ fontSize: 13 }}>Сначала выполни анализ личности</div>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs strip */}
      <div style={{
        display: 'flex', gap: 4, flexShrink: 0,
        padding: '6px 12px',
        background: 'rgba(5,5,13,0.85)',
        borderBottom: `1px solid ${C.border}`,
      }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '6px 20px', borderRadius: 6, cursor: 'pointer',
              fontFamily: font.body, fontSize: 12, letterSpacing: '0.03em',
              background: tab === t.key ? C.accentDim : 'transparent',
              border: `1px solid ${tab === t.key ? 'rgba(180,185,230,0.28)' : 'transparent'}`,
              color: tab === t.key ? C.accent : C.textDim,
              transition: 'all 0.18s',
            }}
          >
            {t.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontFamily: font.mono, fontSize: 9, color: C.textDim, alignSelf: 'center', letterSpacing: '0.06em' }}>
          scroll · pan · drag
        </span>
      </div>

      {/* Map fills rest */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <MapView key={tab} graphData={graphs[tab]} />
      </div>
    </div>
  )
}
